import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import googleSheets from '../../services/googleSheets';
import { Customer } from '../../types';
import { ValidationUtils } from '../../utils/validation';
import MotivationalLoader from '../MotivationalLoader';
import RichTextEditor from './RichTextEditor';
import './AddCustomerForm.css';

interface FormData {
  customerId: string;
  email: string;
  companyName: string;
  segment: Customer['segment'];
  signupDate: string;
  companyGoal: string;
}

interface FormErrors {
  customerId?: string;
  email?: string;
  companyName?: string;
  segment?: string;
  signupDate?: string;
  companyGoal?: string;
}

const AddCustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    customerId: '',
    email: '',
    companyName: '',
    segment: 'small_business',
    signupDate: new Date().toISOString().split('T')[0],
    companyGoal: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Customer ID validation
    if (!formData.customerId.trim()) {
      newErrors.customerId = 'Customer ID is required';
    } else if (!/^\d+$/.test(formData.customerId.trim())) {
      newErrors.customerId = 'Customer ID must be numeric';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!ValidationUtils.validateEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Company name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    // Signup date validation
    if (!formData.signupDate) {
      newErrors.signupDate = 'Signup date is required';
    } else {
      const signupDate = new Date(formData.signupDate);
      const now = new Date();
      if (signupDate > now) {
        newErrors.signupDate = 'Signup date cannot be in the future';
      }
    }

    // Company goal validation (optional but if provided, should have minimum length)
    if (formData.companyGoal.trim()) {
      // Strip HTML tags for length validation
      const textContent = formData.companyGoal.replace(/<[^>]*>/g, '').trim();
      if (textContent.length < 10) {
        newErrors.companyGoal = 'Company goal should be at least 10 characters if provided';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if customer ID already exists
      const existingCustomers = await googleSheets.getAllCustomers();
      const customerExists = existingCustomers.some(c => c.id === formData.customerId.trim());
      
      if (customerExists) {
        setErrors({ customerId: 'A customer with this ID already exists' });
        setLoading(false);
        return;
      }

      // Check if email already exists
      const emailExists = existingCustomers.some(c => c.email.toLowerCase() === formData.email.trim().toLowerCase());
      
      if (emailExists) {
        setErrors({ email: 'A customer with this email already exists' });
        setLoading(false);
        return;
      }

      // Create customer object
      const newCustomer: Customer = {
        id: formData.customerId.trim(),
        email: formData.email.trim(),
        companyName: ValidationUtils.sanitizeInput(formData.companyName.trim()),
        segment: formData.segment,
        signupDate: formData.signupDate,
        onboardingStatus: 'not_started',
        companyGoal: formData.companyGoal.trim(), // Keep HTML formatting for rich text
      };

      // Add customer to Google Sheets
      await googleSheets.addCustomer(newCustomer);

      setSuccess(true);
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  }, [formData, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleReset = useCallback(() => {
    setFormData({
      customerId: '',
      email: '',
      companyName: '',
      segment: 'small_business',
      signupDate: new Date().toISOString().split('T')[0],
      companyGoal: '',
    });
    setErrors({});
    setError(null);
    setSuccess(false);
  }, []);

  if (success) {
    return (
      <div className="add-customer-form success-state">
        <div className="success-message">
          <h2>✅ Customer Added Successfully!</h2>
          <p>The new customer has been added to the onboarding platform.</p>
          <p>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <MotivationalLoader type="general" size="md" />;
  }

  return (
    <div className="add-customer-form">
      <div className="form-header">
        <h1>Add New Customer</h1>
        <p>Add a new customer to the onboarding platform</p>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="customer-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="customerId">
              Customer ID <span className="required">*</span>
            </label>
            <input
              type="text"
              id="customerId"
              value={formData.customerId}
              onChange={(e) => handleInputChange('customerId', e.target.value)}
              placeholder="Enter numeric customer ID"
              className={errors.customerId ? 'error' : ''}
              disabled={loading}
            />
            {errors.customerId && (
              <span className="field-error">{errors.customerId}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="customer@company.com"
              className={errors.email ? 'error' : ''}
              disabled={loading}
            />
            {errors.email && (
              <span className="field-error">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="companyName">
              Company Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Enter company name"
              className={errors.companyName ? 'error' : ''}
              disabled={loading}
            />
            {errors.companyName && (
              <span className="field-error">{errors.companyName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="segment">
              Segment <span className="required">*</span>
            </label>
            <select
              id="segment"
              value={formData.segment}
              onChange={(e) => handleInputChange('segment', e.target.value as Customer['segment'])}
              className={errors.segment ? 'error' : ''}
              disabled={loading}
            >
              <option value="small_business">Small Business</option>
              <option value="enterprise">Enterprise</option>
              <option value="e-commerce">E-commerce</option>
            </select>
            {errors.segment && (
              <span className="field-error">{errors.segment}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="signupDate">
              Signup Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="signupDate"
              value={formData.signupDate}
              onChange={(e) => handleInputChange('signupDate', e.target.value)}
              className={errors.signupDate ? 'error' : ''}
              disabled={loading}
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.signupDate && (
              <span className="field-error">{errors.signupDate}</span>
            )}
          </div>
        </div>

        <div className="form-group full-width">
          <label htmlFor="companyGoal">
            Company Goal
          </label>
          <RichTextEditor
            value={formData.companyGoal}
            onChange={(value) => handleInputChange('companyGoal', value)}
            placeholder="Describe the company's goals and objectives..."
            className={errors.companyGoal ? 'error' : ''}
            readOnly={loading}
          />
          {errors.companyGoal && (
            <span className="field-error">{errors.companyGoal}</span>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-outline"
            disabled={loading}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding Customer...' : 'Add Customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCustomerForm;